import React, { useState } from 'react';
import SEO from './SEO';

const XmlFormatter: React.FC = () => {
  const [inputXml, setInputXml] = useState('');
  const [outputXml, setOutputXml] = useState('');
  const [error, setError] = useState('');
  const [isValid, setIsValid] = useState<boolean | null>(null);

  const formatXml = (indentSize: number = 2) => {
    if (!inputXml.trim()) {
      setError('请输入XML数据');
      return;
    }

    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(inputXml, 'text/xml');
      
      // 检查解析错误
      const parseError = xmlDoc.getElementsByTagName('parsererror');
      if (parseError.length > 0) {
        throw new Error(parseError[0].textContent || 'XML解析错误');
      }

      const formatted = formatXmlString(inputXml, indentSize);
      setOutputXml(formatted);
      setError('');
      setIsValid(true);
    } catch (err) {
      setError(`XML格式错误: ${(err as Error).message}`);
      setOutputXml('');
      setIsValid(false);
    }
  };

  const formatXmlString = (xml: string, indentSize: number): string => {
    const PADDING = ' '.repeat(indentSize);
    const reg = /(>)(<)(\/*)/g;
    let formatted = xml.replace(reg, '$1\r\n$2$3');
    
    let pad = 0;
    return formatted.split('\r\n').map((node) => {
      let indent = 0;
      if (node.match(/.+<\/\w[^>]*>$/)) {
        indent = 0;
      } else if (node.match(/^<\/\w/) && pad > 0) {
        pad -= 1;
      } else if (node.match(/^<\w[^>]*[^/]>.*$/)) {
        indent = 1;
      } else {
        indent = 0;
      }
      
      const padding = PADDING.repeat(pad);
      pad += indent;
      return padding + node;
    }).join('\r\n');
  };

  const minifyXml = () => {
    if (!inputXml.trim()) {
      setError('请输入XML数据');
      return;
    }

    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(inputXml, 'text/xml');
      
      const parseError = xmlDoc.getElementsByTagName('parsererror');
      if (parseError.length > 0) {
        throw new Error(parseError[0].textContent || 'XML解析错误');
      }

      // 简单的压缩：移除多余的空白字符
      const minified = inputXml
        .replace(/\s+</g, '<')
        .replace(/>\s+/g, '>')
        .replace(/\s+/g, ' ')
        .trim();
      
      setOutputXml(minified);
      setError('');
      setIsValid(true);
    } catch (err) {
      setError(`XML格式错误: ${(err as Error).message}`);
      setOutputXml('');
      setIsValid(false);
    }
  };

  const validateXml = () => {
    if (!inputXml.trim()) {
      setError('请输入XML数据');
      setIsValid(null);
      return;
    }

    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(inputXml, 'text/xml');
      
      const parseError = xmlDoc.getElementsByTagName('parsererror');
      if (parseError.length > 0) {
        throw new Error(parseError[0].textContent || 'XML解析错误');
      }
      
      setError('');
      setIsValid(true);
    } catch (err) {
      setError(`XML格式错误: ${(err as Error).message}`);
      setIsValid(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('已复制到剪贴板');
    } catch (err) {
      console.error('复制失败:', err);
      alert('复制失败，请手动复制');
    }
  };

  const clearAll = () => {
    setInputXml('');
    setOutputXml('');
    setError('');
    setIsValid(null);
  };

  const loadSample = () => {
    const sampleXml = `<?xml version="1.0" encoding="UTF-8"?>
<bookstore>
<book id="1" category="fiction">
<title lang="en">Great Gatsby</title>
<author>F. Scott Fitzgerald</author>
<year>1925</year>
<price>10.99</price>
</book>
<book id="2" category="fiction">
<title lang="en">1984</title>
<author>George Orwell</author>
<year>1949</year>
<price>8.99</price>
</book>
<book id="3" category="web">
<title lang="en">Learning XML</title>
<author>Erik T. Ray</author>
<year>2003</year>
<price>39.95</price>
</book>
</bookstore>`;
    setInputXml(sampleXml);
  };

  const extractInfo = (xml: string) => {
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xml, 'text/xml');
      
      const elements = xmlDoc.getElementsByTagName('*');
      const elementCount = elements.length;
      
      // 统计不同类型的节点
      const nodeTypes = new Set();
      for (let i = 0; i < elements.length; i++) {
        nodeTypes.add(elements[i].tagName);
      }
      
      return {
        elementCount,
        uniqueElements: nodeTypes.size,
        hasDeclaration: xml.trim().startsWith('<?xml')
      };
    } catch {
      return null;
    }
  };

  const xmlInfo = outputXml ? extractInfo(outputXml) : null;

  return (
    <div className="tool-container">
      <SEO 
        title="XML格式化工具 - 在线XML美化压缩验证"
        description="免费在线XML格式化工具，支持XML美化、压缩、验证和统计。提供2空格、4空格缩进选项，实时错误检测，标签统计功能。"
        keywords="XML格式化,XML美化,XML压缩,XML验证,XML工具,在线XML编辑器"
      />
      <h1 className="tool-title">XML格式化工具</h1>
      <p className="tool-description">
        格式化、验证和美化XML数据，支持压缩和语法检查
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
        <div className="form-group">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <label className="form-label">输入XML</label>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                className="btn btn-secondary"
                onClick={loadSample}
                style={{ fontSize: '12px', padding: '6px 12px' }}
              >
                加载示例
              </button>
              <button 
                className="btn btn-secondary"
                onClick={clearAll}
                style={{ fontSize: '12px', padding: '6px 12px' }}
              >
                清空
              </button>
            </div>
          </div>
          <textarea
            className="form-textarea"
            value={inputXml}
            onChange={(e) => {
              setInputXml(e.target.value);
              setError('');
              setIsValid(null);
            }}
            placeholder="请输入XML数据..."
            style={{ height: '300px', fontSize: '14px' }}
          />
        </div>

        <div className="form-group">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <label className="form-label">格式化结果</label>
            {outputXml && (
              <button 
                className="btn btn-secondary"
                onClick={() => copyToClipboard(outputXml)}
                style={{ fontSize: '12px', padding: '6px 12px' }}
              >
                复制结果
              </button>
            )}
          </div>
          <textarea
            className="form-textarea"
            value={outputXml}
            readOnly
            placeholder="格式化后的XML将显示在这里..."
            style={{ height: '300px', fontSize: '14px', backgroundColor: '#f8f9fa' }}
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', marginBottom: '20px', flexWrap: 'wrap' }}>
        <button 
          className="btn btn-primary"
          onClick={() => formatXml(2)}
        >
          格式化 (2空格)
        </button>
        <button 
          className="btn btn-primary"
          onClick={() => formatXml(4)}
        >
          格式化 (4空格)
        </button>
        <button 
          className="btn btn-primary"
          onClick={minifyXml}
        >
          压缩XML
        </button>
        <button 
          className="btn btn-secondary"
          onClick={validateXml}
        >
          验证XML
        </button>
      </div>

      {/* 状态显示 */}
      {isValid !== null && (
        <div style={{
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px',
          backgroundColor: isValid ? '#d4edda' : '#f8d7da',
          color: isValid ? '#155724' : '#721c24',
          border: `1px solid ${isValid ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '18px' }}>
              {isValid ? '✅' : '❌'}
            </span>
            <span>
              {isValid ? 'XML格式正确' : 'XML格式错误'}
            </span>
          </div>
        </div>
      )}

      {error && (
        <div style={{
          backgroundColor: '#f8d7da',
          color: '#721c24',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid #f5c6cb',
          fontFamily: 'Courier New, monospace',
          fontSize: '14px'
        }}>
          {error}
        </div>
      )}

      {/* XML信息统计 */}
      {outputXml && xmlInfo && (
        <div className="result-container">
          <h3 className="result-title">XML信息统计</h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '15px'
          }}>
            <div style={{ backgroundColor: 'white', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#667eea' }}>
                {outputXml.split('\n').length}
              </div>
              <div style={{ color: '#7f8c8d', fontSize: '14px' }}>行数</div>
            </div>
            <div style={{ backgroundColor: 'white', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#667eea' }}>
                {outputXml.length}
              </div>
              <div style={{ color: '#7f8c8d', fontSize: '14px' }}>字符数</div>
            </div>
            <div style={{ backgroundColor: 'white', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#667eea' }}>
                {xmlInfo.elementCount}
              </div>
              <div style={{ color: '#7f8c8d', fontSize: '14px' }}>元素数量</div>
            </div>
            <div style={{ backgroundColor: 'white', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#667eea' }}>
                {xmlInfo.uniqueElements}
              </div>
              <div style={{ color: '#7f8c8d', fontSize: '14px' }}>唯一标签</div>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginTop: '30px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
        <h3 style={{ color: '#2c3e50', marginBottom: '15px' }}>功能说明</h3>
        <ul style={{ color: '#7f8c8d', lineHeight: '1.6' }}>
          <li><strong>格式化：</strong> 将压缩的XML数据格式化为易读的形式</li>
          <li><strong>压缩：</strong> 移除所有不必要的空格和换行符</li>
          <li><strong>验证：</strong> 检查XML语法是否正确</li>
          <li><strong>统计：</strong> 显示XML的行数、字符数、元素数量等信息</li>
          <li><strong>复制：</strong> 一键复制格式化后的结果</li>
        </ul>
      </div>
    </div>
  );
};

export default XmlFormatter;